import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    SiteLogo,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    StarIcon
} from "../../anatomy/icons";
import ncoreLogo from "../../assets/ncore-logo.png";

const NAME = "SiteLogo";

const USAGE_CODE = `<SiteLogo
    isWorkWithAction={true}
    onPress={handleGoHome}
    subTitle="UI kit"
    title="NCore"
    size="medium"
/>

<SiteLogo
    imageUrl="https://ncore.nibgat.space/assets/images/ncorelogo.png"
    accessibilityLabel="NCore"
    size="medium"
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const NAVIGATE_DELAY = 1400;

const getItemsStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    justifyContent: "center",
    gap: spaces.spacingLg,
    alignItems: "stretch",
    flexWrap: "wrap",
    display: "flex"
});

const getItemStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    alignItems: "flex-start",
    flexDirection: "column",
    gap: spaces.spacingMd,
    display: "flex"
});

const NODE_STYLE: CSSProperties = {
    alignItems: "center",
    display: "flex",
    flex: 1
};

const getBarStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    padding: `${spaces.spacingSm}px ${spaces.spacingMd}px`,
    gap: spaces.spacingSm,
    alignItems: "center",
    display: "flex"
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("siteLogoPage.markTitle"),
        id: "mark"
    },
    {
        title: t("siteLogoPage.linkTitle"),
        id: "link"
    },
    {
        title: t("siteLogoPage.rowTitle"),
        id: "row"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SiteLogoPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const {
        colors
    } = docsTokens;

    const itemStyle = getItemStyle(docsTokens);
    const itemsStyle = getItemsStyle(docsTokens);

    const [
        isNavigating,
        setIsNavigating
    ] = useState(false);

    const [
        isFlex,
        setIsFlex
    ] = useState(true);

    useEffect(() => {
        if(!isNavigating) {
            return;
        }

        const timer = window.setTimeout(() => setIsNavigating(false), NAVIGATE_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isNavigating
    ]);

    const handleGoHome = () => setIsNavigating(true);

    const handleToggleFlex = () => setIsFlex(!isFlex);

    const logoImageProps = {
        tintColor: colors.content.icon.default
    };

    const renderItem = (label: string, node: ReactNode) => <div
        style={itemStyle}
        key={label}
    >
        <div
            style={NODE_STYLE}
        >
            {node}
        </div>
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("siteLogoPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("siteLogoPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("siteLogoPage.overviewCaption")}
            >
                <div
                    style={itemStyle}
                >
                    <SiteLogo
                        subTitle={t("siteLogoPage.tagline")}
                        title={t("sample.brand")}
                        isLoading={isNavigating}
                        isWorkWithAction={true}
                        onPress={handleGoHome}
                        size="medium"
                    />
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("siteLogoPage.sizesText")}
            </Paragraph>
            <Preview>
                <div
                    style={itemsStyle}
                >
                    {
                        SIZES.map((size) => renderItem(`size="${size}"`, <SiteLogo
                            subTitle={t("siteLogoPage.tagline")}
                            title={t("sample.brand")}
                            size={size}
                        />))
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("siteLogoPage.markTitle")}
            id="mark"
        >
            <Paragraph>
                {t("siteLogoPage.markText")}
            </Paragraph>
            <Preview>
                <div
                    style={itemsStyle}
                >
                    {
                        renderItem(t("siteLogoPage.markDefault"), <SiteLogo
                            title={t("sample.brand")}
                            size="small"
                        />)
                    }
                    {
                        renderItem("icon", <SiteLogo
                            title={t("sample.brand")}
                            icon={StarIcon}
                            size="small"
                        />)
                    }
                    {
                        renderItem("imageUrl", <SiteLogo
                            imageProps={logoImageProps}
                            title={t("sample.brand")}
                            imageUrl={ncoreLogo}
                            size="small"
                        />)
                    }
                    {
                        renderItem("isShowIcon={false}", <SiteLogo
                            title={t("sample.brand")}
                            isShowIcon={false}
                            size="small"
                        />)
                    }
                    {
                        renderItem(t("siteLogoPage.markOnly"), <SiteLogo
                            accessibilityLabel={t("sample.brand")}
                            imageProps={logoImageProps}
                            imageUrl={ncoreLogo}
                            size="small"
                        />)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("siteLogoPage.linkTitle")}
            id="link"
        >
            <Paragraph>
                {t("siteLogoPage.linkText")}
            </Paragraph>
            <Preview>
                <div
                    style={itemsStyle}
                >
                    {
                        renderItem("isWorkWithAction", <SiteLogo
                            subTitle={t("siteLogoPage.tagline")}
                            title={t("sample.brand")}
                            onPress={() => undefined}
                            isWorkWithAction={true}
                            size="medium"
                        />)
                    }
                    {
                        renderItem("isDisabled", <SiteLogo
                            subTitle={t("siteLogoPage.tagline")}
                            title={t("sample.brand")}
                            onPress={() => undefined}
                            isWorkWithAction={true}
                            isDisabled={true}
                            size="medium"
                        />)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("siteLogoPage.rowTitle")}
            id="row"
        >
            <Paragraph>
                {t("siteLogoPage.rowText")}
            </Paragraph>
            <Preview
                caption={t("siteLogoPage.rowCaption")}
                isStacked={true}
            >
                <DemoFrame>
                    <div
                        style={getBarStyle(docsTokens)}
                    >
                        <SiteLogo
                            subTitle={t("siteLogoPage.tagline")}
                            title={t("sample.brand")}
                            onPress={() => undefined}
                            isWorkWithFlex1={isFlex}
                            isWorkWithAction={true}
                            size="medium"
                        />
                        <Button
                            accessibilityLabel={t("sample.search")}
                            onPress={() => undefined}
                            spreadBehaviour="free"
                            icon={SearchIcon}
                            variant="ghost"
                            type="neutral"
                        />
                    </div>
                </DemoFrame>
                <Switch
                    title={t("siteLogoPage.rowSwitch")}
                    onPress={handleToggleFlex}
                    isChecked={isFlex}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("siteLogoPage.a11yName"),
                    t("siteLogoPage.a11yLink"),
                    t("siteLogoPage.a11yKeyboard"),
                    t("siteLogoPage.a11yState"),
                    t("siteLogoPage.a11ySize")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("siteLogoPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("siteLogoPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("siteLogoPage.propTitleKey"),
                        type: "keyof Translation",
                        name: "titleKey"
                    },
                    {
                        description: t("siteLogoPage.propSubTitleKey"),
                        type: "keyof Translation",
                        name: "subTitleKey"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("siteLogoPage.propSize"),
                        defaultValue: "\"large\"",
                        name: "size"
                    },
                    {
                        description: t("siteLogoPage.propIsWorkWithAction"),
                        name: "isWorkWithAction",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("siteLogoPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("siteLogoPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("siteLogoPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("siteLogoPage.propIsShowIcon"),
                        defaultValue: "true",
                        name: "isShowIcon",
                        type: "boolean"
                    },
                    {
                        description: t("siteLogoPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("siteLogoPage.propIconColor"),
                        type: "keyof ProjectColorPalette",
                        name: "iconColor"
                    },
                    {
                        description: t("siteLogoPage.propImageUrl"),
                        name: "imageUrl",
                        type: "string"
                    },
                    {
                        description: t("siteLogoPage.propImageSource"),
                        type: "ImageURISource",
                        name: "imageSource"
                    },
                    {
                        description: t("siteLogoPage.propImage"),
                        type: "ComponentType<ImageProps>",
                        name: "image"
                    },
                    {
                        description: t("siteLogoPage.propImageProps"),
                        type: "ImageProps",
                        name: "imageProps"
                    },
                    {
                        description: t("siteLogoPage.propImageSpace"),
                        type: "keyof Spaces",
                        name: "imageSpace"
                    },
                    {
                        description: t("siteLogoPage.propIsCustomImageSize"),
                        name: "isCustomImageSize",
                        type: "boolean"
                    },
                    {
                        description: t("siteLogoPage.propIsShowBorder"),
                        defaultValue: "false",
                        name: "isShowBorder",
                        type: "boolean"
                    },
                    {
                        type: "keyof ContainerContentColors | \"transparent\"",
                        description: t("siteLogoPage.propBackgroundColor"),
                        name: "backgroundColor"
                    },
                    {
                        type: "keyof BorderContentColors | \"transparent\"",
                        description: t("siteLogoPage.propBorderColor"),
                        name: "borderColor"
                    },
                    {
                        description: t("siteLogoPage.propLogoBackgroundColor"),
                        type: "keyof ContainerContentColors",
                        defaultValue: "\"default\"",
                        name: "logoBackgroundColor"
                    },
                    {
                        description: t("siteLogoPage.propIsWorkWithFlex1"),
                        name: "isWorkWithFlex1",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("siteLogoPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("siteLogoPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("siteLogoPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("siteLogoPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SiteLogoPage;
